import React, { useEffect } from 'react';
import { IonButton, IonContent, IonText } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { RevenueCatModel } from './revenue-cat/RevenueCatModel';
import { PaywallDialogViewModel } from './PaywallDialogViewModel';
import { PaymentTermButton } from './PaymentTermsButton';
import { TrackingService } from '../support/tracking/TrackingService';

export const PromotionDialogContent: React.FC<{onUpgrade: () => void, onRestore: () => void}> = ({ onUpgrade, onRestore }) => {

    const revenueCat = useInstance(RevenueCatModel);
    useSubscription(() => revenueCat);

    const dialogVM = useInstance(PaywallDialogViewModel);
    useSubscription(() => dialogVM);

    const term = dialogVM.state.paymentTerm;
    const buttonMessage = (term === 'annual' ? 'Renews at $39.99 after 1 year, cancel any time' : 'Cancel any time');
    const buttonText = (term === 'annual' ? 'Claim Limited Offer' : 'Continue');

    const tracking = useInstance(TrackingService);

    useEffect(() => {
        if (!revenueCat.hasUpgraded()) {
            tracking.track('Paywall Opened', () => ({
                from: dialogVM.getTriggerSource(),
                paywallTitle: 'Year End Offer',
                annualPrice: 23.99,
                monthlyPrice: 3.99,
                buttonText
            }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onContinue = () => {
        tracking.track('Paywall Continued', () => ({
            term,
            from: dialogVM.getTriggerSource(),
            paywallTitle: 'Year End Offer',
            annualPrice: 23.99,
            monthlyPrice: 3.99,
            buttonText
        }));
        onUpgrade();
    };

    return (
        <IonContent style={{'--background': 'linear-gradient(168.3deg, #5394BA 0.8%, #84BCA7 32.57%, #B2DD9F 96.11%)'}}>
            <div style={{height: '100%', maxWidth: 390, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-around', padding: 'calc(var(--ion-safe-area-top) + 25px) 20px max(var(--ion-safe-area-bottom), 25px)'}}>

                <img style={{padding: '10px 55px', minHeight: 0, flex: '100 100 0'}} src="./assets/images/paywall/year-end-offer.svg" alt="Year End Offer" />

                <div style={{width: '100%', flex: '5 0 auto'}}>
                    <PaymentTermButton
                        disabled={dialogVM.state.isWorking}
                        isActive={term === 'annual'}
                        onClick={() => dialogVM.setState({ paymentTerm: 'annual' })}
                        termLabel={`Year End Offer 🎁`}
                        termDescription={<>1 year <s>$39.99</s> $23.99 (only $2/month)</>}
                        discountBadge={`SAVE ${dialogVM.getPromotionSaving()}`}
                    />

                    <PaymentTermButton
                        disabled={dialogVM.state.isWorking}
                        isActive={term === 'monthly'}
                        onClick={() => dialogVM.setState({ paymentTerm: 'monthly' })}
                        termLabel="Monthly Plan — $3.99"
                        termDescription="Trial, then $3.99, billed monthly"
                    />
                </div>

                <div style={{width: '100%', flex: '0 0 auto'}}>
                    <IonButton
                        disabled={dialogVM.state.isWorking}
                        expand="block"
                        fill="solid"
                        color="primary"
                        onClick={() => onContinue()}
                        data-id="continue"
                    >
                        <span>{buttonText}</span>
                    </IonButton>

                    <div style={{width: '100%', textAlign: 'center'}}>
                        <IonText color="medium"><small>{buttonMessage}</small></IonText>
                    </div>

                    <div style={{width: '100%', maxWidth: 260, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <IonButton disabled={dialogVM.state.isWorking} size="small" expand="block" fill="clear" color="secondary" href="https://www.autopylot.io/terms" target="_blank">Terms of Use</IonButton>
                        {revenueCat.canRestore() &&
                            <IonButton expand="block" size="small" fill="clear" color="secondary" onClick={onRestore} data-id="restore">Restore Purchases</IonButton>
                        }
                    </div>
                </div>

            </div>
        </IonContent>
    );
};
